import React, { useEffect, useMemo, useState } from "react";
import { Title } from "../../../stories/Title/Title";
import { DentalSvgWithButtons } from "../../../stories/DentalSvgWithButtons/DentalSvgWithButtons";
import { Controller, useFormContext } from "react-hook-form";
import { Product } from "../../../types/interfaces";
import { getDisabledTeeth } from "../../../utils/workflows.utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Button } from "../../../stories/Button/Button";

interface PartialDentureInfoProps {
  product: Product;
}

const ALL_MISSING_LABEL = "Remplacer toutes les dents manquantes";
const ALL_MISSING_COLOR = "#2f9e44";

export const PartialDentureInfo = ({ product }: PartialDentureInfoProps) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const showReplaceAllMissingTeethButton =
    useFeatureFlagEnabled("pap_replace_all_missing_teeth") ?? false;

  const formTeeths = watch("teeths");
  const currentMissingTeeth =
    formTeeths?.missingTeeth ??
    product?.removableItem?.reduce((acc, curr) => {
      return acc.concat(curr?.partialDenture?.missingTeeth ?? []);
    }, []) ??
    [];

  const currentToExtract =
    formTeeths?.toExtract ??
    product?.removableItem?.reduce((acc, curr) => {
      return acc.concat(curr?.teethToManufacture ?? []);
    }, []) ??
    [];

  // Forces DentalSvgWithButtons to remount when toggling "all missing teeth",
  // as its internal state doesn't sync with form values
  const [resetKey, setResetKey] = useState(0);

  const allPossibleTeeth = useMemo(() => {
    return product.teeth || [];
  }, [product.teeth]);

  const isAllMissingSelected = useMemo(() => {
    return (
      allPossibleTeeth.length > 0 &&
      allPossibleTeeth.every(t => currentMissingTeeth.includes(t))
    );
  }, [allPossibleTeeth, currentMissingTeeth]);

  const hasManualSelection = useMemo(() => {
    return (
      (currentMissingTeeth.length > 0 || currentToExtract.length > 0) &&
      !isAllMissingSelected
    );
  }, [currentMissingTeeth, currentToExtract, isAllMissingSelected]);

  const dentalArchesSvg = useMemo(() => {
    const base = [
      {
        label: "Dents déjà manquantes",
        teeth: isAllMissingSelected ? [] : currentMissingTeeth,
        color: "black",
        description: "La dent est déjà absente en bouche du patient.",
        key: "missing-teeth",
        name: "missingTeeth",
        disabled: isAllMissingSelected,
      },
      {
        label: "Extraites le jour de la pose",
        teeth: isAllMissingSelected ? [] : currentToExtract,
        color: "#3C61E3",
        description:
          "La dent/racine sera extraite le jour de la pose et doit être incluse sur la prothèse partielle",
        key: "to-extract",
        name: "toExtract",
        disabled: isAllMissingSelected,
      },
    ];
    if (!showReplaceAllMissingTeethButton) return base;

    return [
      ...base,
      {
        label: ALL_MISSING_LABEL,
        teeth: isAllMissingSelected ? allPossibleTeeth : [],
        color: ALL_MISSING_COLOR,
        description:
          "Toutes les dents de l'arcade sont manquantes et doivent être remplacées.",
        key: "all-missing",
        name: "missingTeeth",
        hideButton: true,
      },
    ];
  }, [
    currentMissingTeeth,
    currentToExtract,
    isAllMissingSelected,
    showReplaceAllMissingTeethButton,
  ]);

  const disabledTeeth = useMemo(
    () => getDisabledTeeth(product.teeth || []) ?? [],
    [JSON.stringify(product.teeth ?? [])]
  );

  const handleToggleAllMissing = () => {
    setValue("teeths", {
      missingTeeth: isAllMissingSelected ? [] : allPossibleTeeth,
      toExtract: [],
    });
    setResetKey(prev => prev + 1);
  };

  useEffect(() => {
    if (product?.removableItem?.[0]?.partialDenture) {
      setValue("teeths", {
        toExtract: product.removableItem.reduce((acc, curr) => {
          return acc.concat(curr?.teethToManufacture ?? []);
        }, []),
        missingTeeth: product.removableItem.reduce((acc, curr) => {
          return acc.concat(curr?.partialDenture?.missingTeeth ?? []);
        }, []),
      });
    } else {
      setValue("teeths", {
        toExtract: [],
        missingTeeth: [],
      });
    }

    // Force remount of DentalSvgWithButtons to reset its internal useState
    // (DentalSvgWithButtons doesn't sync with prop changes after mount)
    setResetKey(prev => prev + 1);
  }, [product]);

  return (
    <div>
      <Title
        title="Information sur votre prothèse amovible partielle"
        size={3}
        mb="md"
      />
      <Title title="Sélectionnez les dents à inclure dans la PAP" size={5} />
      <Controller
        control={control}
        name="teeths"
        rules={{
          validate: value =>
            (value?.toExtract?.length ?? 0) > 0 ||
            (value?.missingTeeth?.length ?? 0) > 0 ||
            "Veuillez sélectionner au moins une dent",
        }}
        render={({ field }) => {
          return (
            <DentalSvgWithButtons
              key={resetKey}
              teethChoices={dentalArchesSvg}
              disabledTeeth={disabledTeeth}
              onClick={value => {
                const filtered = value.filter(
                  tooth => tooth.name !== "allMissing"
                );
                const result = filtered.reduce(
                  (acc, tooth) => ({ ...acc, [tooth.name]: tooth.teeth }),
                  {} as Record<string, number[]>
                );
                field.onChange(result);
              }}
              dataTestId="partial-denture-teeth-selection"
              extraButtons={
                showReplaceAllMissingTeethButton ? (
                  <Button
                    variant={
                      isAllMissingSelected
                        ? "validation"
                        : hasManualSelection
                          ? "disabled"
                          : "outline"
                    }
                    color={ALL_MISSING_COLOR}
                    onClick={
                      hasManualSelection ? undefined : handleToggleAllMissing
                    }
                    dataTestId="partial-denture-replace-all-missing-teeth"
                  >
                    {isAllMissingSelected
                      ? "Désélectionner toutes les dents manquantes"
                      : "Remplacer toutes les dents manquantes"}
                  </Button>
                ) : undefined
              }
            />
          );
        }}
      />
      {errors.teeths && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.teeths.message as string}
        </span>
      )}
    </div>
  );
};
